/**
 * Reviews Renderer
 * Dynamically renders product reviews after page load
 * Reads review data from product metafields passed via data attribute
 */

(function () {
  'use strict';

  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Format date for display
   * @param {string} dateString - ISO date string
   * @returns {string} Formatted date
   */
  function formatDate(dateString) {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;

      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (e) {
      return '';
    }
  }

  /**
   * Render photo gallery for a review
   * @param {Array} images - Array of image objects with src and alt
   * @param {number} reviewIndex - Index of the review
   * @returns {string} HTML string for photos
   */
  function renderPhotos(images, reviewIndex) {
    if (!images || !Array.isArray(images) || images.length === 0) {
      return '';
    }

    // Limit to max 3 photos per review
    const limitedImages = images.slice(0, 3);

    const photoElements = limitedImages
      .map(
        (image, photoIndex) => `
      <img
        src="${escapeHtml(image.src || image)}"
        alt="${escapeHtml(image.alt || 'Review photo')}"
        loading="lazy"
        class="review-photo"
        data-lightbox="review-${reviewIndex}"
        data-photo-index="${photoIndex}"
        tabindex="0"
        role="button"
        aria-label="View photo ${photoIndex + 1} of ${limitedImages.length}"
      >
    `
      )
      .join('');

    return `<div class="review-photos">${photoElements}</div>`;
  }

  /**
   * Render business reply if available
   * @param {Object} reply - Reply object with comment and date
   * @returns {string} HTML string for reply
   */
  function renderReply(reply) {
    if (!reply || !reply.comment) return '';

    return `
      <div class="review-reply">
        <div class="review-reply-header">
          <span>Business Response</span>
          ${reply.updateTime ? `<span class="review-date">${formatDate(reply.updateTime)}</span>` : ''}
        </div>
        <p class="review-reply-text">${escapeHtml(reply.comment)}</p>
      </div>
    `;
  }

  /**
   * Render a single review item
   * @param {Object} entry - Review entry from metafield
   * @param {number} index - Review index
   * @returns {string} HTML string for review
   */
  function renderReviewItem(entry, index) {
    const rating = parseInt(entry.rating, 10) || 5;
    const name = entry.name || 'Anonymous';
    const review = entry.review || '';
    const images = entry.image || [];
    const date = entry.createTime || entry.date || '';
    const reply = entry.reviewReply || entry.reply || null;

    return `
      <article class="review-item" id="review-${index}">
        <div class="review-header">
          <span class="reviewer-name">${escapeHtml(name)}</span>
          <span
            class="rating-star"
            style="--rating: ${rating}; --rating-max: 5; --rating-decimal: 0;"
            role="img"
            aria-label="${rating} out of 5 stars"
          ></span>
          ${date ? `<time class="review-date" datetime="${escapeHtml(date)}">${formatDate(date)}</time>` : ''}
        </div>
        ${review ? `<p class="review-comment">${escapeHtml(review)}</p>` : ''}
        ${renderPhotos(images, index)}
        ${renderReply(reply)}
      </article>
    `;
  }

  /**
   * Render all reviews
   * @param {Array} reviews - Array of review entries
   * @param {HTMLElement} container - Container element
   */
  function renderReviews(reviews, container) {
    if (!reviews || !Array.isArray(reviews) || reviews.length === 0) {
      container.innerHTML = '<p class="reviews-empty">No reviews yet.</p>';
      return;
    }

    const reviewsHtml = reviews.map((entry, index) => renderReviewItem(entry, index)).join('');

    container.innerHTML = reviewsHtml;

    // Initialize lightbox after rendering
    initLightbox(container);
  }

  /**
   * Create and initialize lightbox functionality
   * @param {HTMLElement} container - Reviews container
   */
  function initLightbox(container) {
    // Create lightbox element if it doesn't exist
    let lightbox = document.querySelector('.reviews-lightbox');
    if (!lightbox) {
      lightbox = document.createElement('div');
      lightbox.className = 'reviews-lightbox';
      lightbox.innerHTML = `
        <div class="reviews-lightbox-content">
          <button class="reviews-lightbox-close" aria-label="Close">&times;</button>
          <img class="reviews-lightbox-image" src="" alt="">
        </div>
      `;
      document.body.appendChild(lightbox);

      // Close on background click
      lightbox.addEventListener('click', function (e) {
        if (e.target === lightbox || e.target.classList.contains('reviews-lightbox-close')) {
          closeLightbox();
        }
      });

      // Close on escape key
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && lightbox.classList.contains('active')) {
          closeLightbox();
        }
      });
    }

    // Add click handlers to photos
    const photos = container.querySelectorAll('.review-photo');
    photos.forEach((photo) => {
      photo.addEventListener('click', function () {
        openLightbox(this.src, this.alt);
      });

      // Keyboard support
      photo.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openLightbox(this.src, this.alt);
        }
      });
    });
  }

  /**
   * Open lightbox with image
   * @param {string} src - Image source
   * @param {string} alt - Image alt text
   */
  function openLightbox(src, alt) {
    const lightbox = document.querySelector('.reviews-lightbox');
    const image = lightbox.querySelector('.reviews-lightbox-image');

    image.src = src;
    image.alt = alt || 'Review photo';
    lightbox.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Focus close button for accessibility
    const closeBtn = lightbox.querySelector('.reviews-lightbox-close');
    if (closeBtn) closeBtn.focus();
  }

  /**
   * Close lightbox
   */
  function closeLightbox() {
    const lightbox = document.querySelector('.reviews-lightbox');
    if (lightbox) {
      lightbox.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  /**
   * Initialize reviews renderer
   */
  function init() {
    const container = document.getElementById('reviews-container');
    if (!container) return;

    // Get reviews data from script tag
    const reviewsScript = document.getElementById('reviews-data');
    if (!reviewsScript) {
      container.innerHTML = '<p class="reviews-empty">No reviews available.</p>';
      return;
    }

    try {
      const reviews = JSON.parse(reviewsScript.textContent);
      renderReviews(reviews, container);
    } catch (e) {
      console.error('Error parsing reviews data:', e);
      container.innerHTML = '<p class="reviews-empty">Unable to load reviews.</p>';
    }
  }

  // Initialize on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded
    init();
  }
})();